(function () {
  const RESUME_DELAY = 3000;
  const DRAG_THRESHOLD = 6;

  class ReviewsMarqueeDrag extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.reviews-marquee__track');
      this.group = this.track ? this.track.querySelector('.reviews-marquee__group') : null;
      this.directionSign = this.dataset.direction === 'right' ? -1 : 1;
      this.speedSeconds = parseFloat(this.dataset.speed) || 40;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.offset = 0;
      this.groupWidth = 0;
      this.paused = false;
      this.hovering = false;
      this.dragging = false;
      this.dragAxis = null;
      this.resumeTimeout = null;
      this.lastTime = null;
    }

    connectedCallback() {
      if (!this.track || !this.group) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => (this.hovering = true));
        this.addEventListener('mouseleave', () => (this.hovering = false));
      }

      // Drag-to-scroll: covers touch swipes on mobile and click-drag with a mouse.
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.addEventListener('pointercancel', this.onPointerUp.bind(this));

      // Trackpad two-finger left/right swipe (sends wheel events with deltaX).
      this.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

      // Without this, a mouse-drag on a review image starts the browser's
      // native "drag the image" ghost instead of our own drag-to-scroll.
      this.addEventListener('dragstart', (event) => event.preventDefault());

      // The 5 duplicated groups already give plenty of buffer, so this only
      // needs to track the real group's width (re-fires once CSS settles).
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.group);

      if (!this.reducedMotion) {
        this.rafId = requestAnimationFrame(this.tick.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      clearTimeout(this.resumeTimeout);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    handleResize() {
      const width = this.group.getBoundingClientRect().width;
      if (width <= 0 || width === this.groupWidth) return;
      this.groupWidth = width;
      this.offset = this.normalize(this.offset);
    }

    normalize(offset) {
      if (!this.groupWidth) return 0;
      return ((offset % this.groupWidth) + this.groupWidth) % this.groupWidth;
    }

    render() {
      this.track.style.transform = `translateX(${-this.offset}px)`;
    }

    tick(time) {
      if (this.lastTime === null) this.lastTime = time;
      const delta = time - this.lastTime;
      this.lastTime = time;

      if (this.groupWidth > 0 && !this.paused && !(this.pauseOnHover && this.hovering)) {
        const pxPerMs = this.groupWidth / (this.speedSeconds * 1000);
        this.offset = this.normalize(this.offset + this.directionSign * pxPerMs * delta);
        this.render();
      }

      this.rafId = requestAnimationFrame(this.tick.bind(this));
    }

    onPointerDown(event) {
      if ((event.pointerType === 'mouse' && event.button !== 0) || !this.groupWidth) return;

      this.dragging = true;
      this.dragAxis = null;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartOffset = this.offset;

      this.paused = true;
      clearTimeout(this.resumeTimeout);
      this.classList.add('is-dragging');
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      const dx = event.clientX - this.dragStartX;
      const dy = event.clientY - this.dragStartY;

      if (this.dragAxis === null) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        this.dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this.dragAxis === 'y') {
          // Mostly-vertical gesture: leave it to the page to scroll.
          this.dragging = false;
          this.paused = false;
          this.classList.remove('is-dragging');
          return;
        }
        this.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();

      let next = this.dragStartOffset - dx;
      // Keep the running value bounded during a long drag (the track is
      // periodic, so folding a whole loop in/out never changes what's on screen).
      while (next < 0) {
        next += this.groupWidth;
        this.dragStartOffset += this.groupWidth;
      }
      while (next >= this.groupWidth * 2) {
        next -= this.groupWidth;
        this.dragStartOffset -= this.groupWidth;
      }

      this.offset = next;
      this.render();
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.offset = this.normalize(this.offset);
      this.pauseTemporarily();
    }

    onWheel(event) {
      if (!this.groupWidth || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      event.preventDefault();
      this.offset = this.normalize(this.offset + event.deltaX);
      this.render();
      this.pauseTemporarily();
    }

    pauseTemporarily() {
      this.paused = true;
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.paused = false;
        this.offset = this.normalize(this.offset);
      }, RESUME_DELAY);
    }
  }

  customElements.define('reviews-marquee-drag', ReviewsMarqueeDrag);
})();
